import React, { useCallback, useState, FC } from 'react';
import { useDropzone } from 'react-dropzone';
import Previeew from '../../assets/Preview.svg';
import { toast } from 'react-toastify';
import { getRandomID } from 'functions';
import Styles from './Styles.module.scss';
import { SVG } from 'Common/commonsvg';
import { ASSETS_URI } from '../../Constant';

// `image` reaches us in different shapes depending on the parent: a url string, an array of
// urls (custom fields store the upload response), a File, or an empty array. Boil it down to a
// url string so nothing downstream has to guess.
const resolveSrc = (img: any): string => {
  if (!img) return '';
  if (typeof img === 'string') return img;
  if (Array.isArray(img)) return resolveSrc(img[0]);
  if (typeof img === 'object') return resolveSrc(img.preview || img.url || img.path);
  return '';
};

// Pull the file extension out of the src itself (strips any query string / hash)
const getExtension = (src: string) => {
  if (typeof src !== 'string') return '';
  const path = src.split('?')[0].split('#')[0];
  const parts = path.split('.');
  return parts.length > 1 ? parts[parts.length - 1].toLowerCase() : '';
};

// Placeholder to show when the preview src fails to load
const getPreviewFallback = (src: string) => {
  const extension = getExtension(src);

  if (extension === 'pdf') {
    return '/manage/images/pdf-file.png';
  }
  if (['jpg', 'jpeg', 'png', 'gif'].includes(extension)) {
    // relative path from the API — retry it against the assets host
    return /^(https?:|blob:|data:|\/)/.test(src)
      ? '/manage/images/document.png'
      : `${ASSETS_URI}/${src}`;
  }
  if (['doc', 'docx'].includes(extension)) {
    return '/manage/images/docs.png';
  }
  if (['xls', 'xlsx'].includes(extension)) {
    return '/manage/images/xlsx.png';
  }
  if (['ppt', 'pptx'].includes(extension)) {
    return '/manage/images/pptx.png';
  }
  return '/manage/images/document.png';
};

interface DropZoneProps {
  setimage: (file: any) => void;
  DropZoneText?: string; // Corrected to lowercase 'string'
  previewimage: Boolean;
  Setimagebool?: (open: boolean) => void;
  classname?: String;
  onClick?: (file: any) => void;
  accept: 'pdf' | 'image' | 'csv' | 'excel' | 'video' | 'none';
  previewposition?: 'top' | 'bottom' | 'inside' | 'original';
  image: string;
  dimensions: {
    height: number;
    width: number;
  };
  maxsize: number | 'null';
}

const DropZoneMultiC: FC<DropZoneProps> = ({
  setimage,
  DropZoneText,
  previewimage = true,
  classname = '',
  onClick,
  previewposition = 'top',
  image,
  dimensions,
  Setimagebool,
  accept = 'none',
  maxsize = null,
}) => {
  const checkDimensions = (file, dimensions, callback) => {
    const img = new Image();
    img.src = window.URL.createObjectURL(file);
    img.onload = () => {
      if (img.width <= dimensions.width && img.height <= dimensions.height) {
        callback(true);
      } else {
        // toast.error(`Logo image should be ${dimensions.width}w x ${dimensions.height}h px size`);
        callback(false);
      }
    };
  };

  const checkFormat = (file: File) => {
    switch (accept) {
      case 'csv':
        if (file.type !== 'text/csv') {
          toast.error('Only CSV files are allowed');
          return false;
        }
        break;
      case 'excel':
        // Adjust this condition based on the Excel types you want to allow
        if (file.type !== 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet') {
          toast.error('Only Excel files are allowed');
          return false;
        }
        break;
      case 'pdf':
        if (file.type !== 'application/pdf') {
          toast.error('Only PDF files are allowed');
          return false;
        }
        break;
      case 'image':
        // Adjust this condition based on the image types you want to allow
        if (!file.type.startsWith('image/')) {
          toast.error('Only image files are allowed');
          return false;
        }
        break;
      default:
        // No specific format restriction for other cases
        break;
    }

    // If none of the above conditions triggered, or for the default case, return true
    return true;
  };
  function getAcceptForType(type: string) {
    switch (type) {
      case 'image':
        return {
          'image/png': ['.png'],
          'image/jpeg': ['.jpg', '.jpeg'],
          'image/webp': ['.webp'],
          'image/gif': ['.gif'],
      // 'image/tiff': ['.tif', '.tiff'],
        };
      case 'pdf':
        return {
          'application/pdf': ['.pdf'],
        };
      case 'excel':
        return {
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
          'application/vnd.ms-excel': ['.xls'],
        };
      case 'word':
        return {
          'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
          'application/msword': ['.doc'],
        };
      case 'text':
        return {
          'text/plain': ['.txt'],
        };
      case 'html':
        return {
          'text/html': ['.html', '.htm'],
        };
      case 'video':
        return {
          'video/mp4': ['.mp4'],
          'video/webm': ['.webm'],
          'video/ogg': ['.ogg'],
        };
      case 'audio':
        return {
          'audio/mpeg': ['.mp3'],
          'audio/wav': ['.wav'],
          'audio/ogg': ['.ogg'],
        };
      default:
        // Accept all
        return {};
    }
  }
  const [preview, setPreview] = useState<File[]>([]);
  const [previewname, setpreviewname] = useState<Array<String>>([]);
  const { getRootProps, getInputProps, isDragActive, isDragReject } = useDropzone({
    ...(accept !== 'none' ? { accept: getAcceptForType(accept) } : {}),
    // ...(maxsize !== null && maxsize !== 'null' && maxsize ? { maxSize: maxsize } : {}),
    onDrop: (acceptedFiles: File[]) => {
      if (acceptedFiles.length > 0) {
        const validFiles = acceptedFiles.filter((file) => {
          // FORMAT CHECK
          if (!checkFormat(file)) return false;

          // SIZE CHECK
          if (maxsize && maxsize !== 'null') {
            const maxBytes = maxsize;
            if (file.size > maxBytes) {
              toast.error(`File "${file.name}" is too large. Max allowed: ${maxsize / (1024 * 1024)} MB`);
              return false;
            }
          }

          return true;
        });

        if (validFiles.length === 0) {
          // toast.error('No valid files accepted');
          return;
        }

        // Continue with dimension checks
        if (dimensions) {
          validFiles.forEach((file) => {
            checkDimensions(file, dimensions, (isDimensionValid) => {
              if (!isDimensionValid) {
                toast.error(
                  `File "${file.name}" has invalid dimensions. ` +
                    `Required: ${dimensions.width}x${dimensions.height}px`,
                );
                return;
              }

              // If valid → add preview & setimage
              if (previewposition === 'inside') {
                setimage(file);
                setpreviewname((prev) => [...prev, file.name]);
              } else {
                setPreview((prev) => [
                  ...prev,
                  Object.assign(file, { preview: URL.createObjectURL(file) }),
                ]);
                setimage(file);
              }
              Setimagebool && Setimagebool(true);
            });
          });
        } else {
          // no dimension check
          validFiles.forEach((file) => {
            if (previewposition === 'inside') {
              setimage(file);
              setpreviewname((prev) => [...prev, file.name]);
            } else {
              setPreview((prev) => [
                ...prev,
                Object.assign(file, { preview: URL.createObjectURL(file) }),
              ]);
            }
          });

          setimage(validFiles);
        }
      } else {
        toast.error('No files accepted');
      }
    },
  });
  console.log(isDragReject, 'isDragRejectisDragReject');

  const handelClear = (index: number) => {
    // compute the remaining files here instead of using an updater — `setimage` is not
    // always a React setState (some parents pass a plain handler that receives the files)
    const remaining = preview.filter((_, i) => i !== index);
    setPreview(remaining);
    setimage(remaining.length > 0 ? remaining : null);
    Setimagebool && Setimagebool(remaining.length > 0);
  };

  const imageSrc = resolveSrc(image);

  // no local preview to splice — this clears the image coming in from the parent
  const handelClearImage = () => {
    setPreview([]);
    setimage(null);
    Setimagebool && Setimagebool(false);
  };
  return (
    <React.Fragment>
      <div className={`justify-content-center ${classname}`}>
        {previewimage && previewposition === 'top' && (
          <div className="imagePreview imagePreview-custom d-flex justify-content-center mb-3">
            {preview.length > 0 ? (
              preview.map((upFile: any, index) => {
                // Specify the correct type 'File'
                return (
                  <div className="previewImage" key={index}>
                    <img src={upFile.preview} alt="preview" />
                  </div>
                );
              })
            ) : (
              <div className="previewImage">
                <img
                  src={imageSrc || Previeew}
                  alt="preview"
                  height="100%"
                  width="100%"
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    // Prevent infinite loop if the fallback also fails
                    target.onerror = null;
                    target.src = getPreviewFallback(imageSrc);
                  }}
                />
              </div>
            )}
          </div>
        )}
        {image && previewname.length > 0 && previewimage && previewposition === 'bottom' ? (
          <div
            className={`row justify-content-center ${classname === '' ? '' : 'dropzone-border'} `}
          >
            {previewname.length > 0 && (
              <div className="">
                {previewname.map((upFile: any, index) => {
                  return (
                    <div className={`${classname === '' ? 'inputDrops custom-drop' : 'boarder'} `}>
                      <div>
                        <div className="d-flex justify-content-between align-items-center dropzon-innerpreview">
                          <img src="" height={10} width={10} alt="excel" />
                          <span>{upFile}</span>
                          <h6 onClick={handelClear} className="m-0">
                            {' '}
                            X{' '}
                          </h6>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        ) : (
          <div
            className={`row justify-content-center  ${classname === '' ? '' : 'dropzone-border'} `}
          >
            <div
              {...getRootProps()}
              className={`${classname === '' ? 'inputDrops custom-drop' : 'boarder'} hoverstyle`}
            >
              <div>
                <input {...getInputProps()} />

                <div className="d-flex justify-content-center">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="44"
                    height="32"
                    viewBox="0 0 44 32"
                    fill="none"
                  >
                    <path
                      d="M11 32C7.96667 32 5.375 30.95 3.225 28.85C1.075 26.75 0 24.1833 0 21.15C0 18.55 0.783333 16.2333 2.35 14.2C3.91667 12.1667 5.96667 10.8667 8.5 10.3C9.33333 7.23333 11 4.75 13.5 2.85C16 0.95 18.8333 0 22 0C25.9 0 29.2083 1.35833 31.925 4.075C34.6417 6.79167 36 10.1 36 14C38.3 14.2667 40.2083 15.2583 41.725 16.975C43.2417 18.6917 44 20.7 44 23C44 25.5 43.125 27.625 41.375 29.375C39.625 31.125 37.5 32 35 32H24V17.7L25.8 19.45C26.1667 19.8167 26.625 20 27.175 20C27.725 20 28.2 19.8 28.6 19.4C28.9667 19.0333 29.15 18.5667 29.15 18C29.15 17.4333 28.9667 16.9667 28.6 16.6L23.4 11.4C23 11 22.5333 10.8 22 10.8C21.4667 10.8 21 11 20.6 11.4L15.4 16.6C15.0333 16.9667 14.8417 17.425 14.825 17.975C14.8083 18.525 15 19 15.4 19.4C15.7667 19.7667 16.225 19.9583 16.775 19.975C17.325 19.9917 17.8 19.8167 18.2 19.45L20 17.7V32H11Z"
                      fill=""
                    />
                  </svg>
                </div>
                <div className="d-flex justify-content-center dropzone-font-size">
                  <p>
                    Drag and drop file here or <span className="color-underline">Choose file</span>
                  </p>
                </div>
                <div className="d-flex justify-content-center DropzoneText">
                  <p>
                    {DropZoneText.split('\n').map((line, index) => (
                      <React.Fragment key={index}>
                        {line}
                        <br />
                      </React.Fragment>
                    ))}
                  </p>
                </div>
              </div>
              {isDragReject && 'File type not accepted, sorry!'}
            </div>
          </div>
        )}
        {previewimage && previewposition === 'original' && (
          <div className={Styles.CustomPreview} style={{ overflowX: 'auto' }}>
            {/* {previewname.map((upFile: any, index) => {
            return ( */}
            {(image || preview.length > 0) &&
              (preview.length > 0 ? (
                preview.map((upFile: any, index) => {
                  // Specify the correct type 'File'
                  return (
                    <div className={`${classname === '' && 'pb-2'} `}>
                      <div>
                        <div
                          className={` ${Styles.previewlook} d-flex justify-content-between align-items-center dropzon-innerpreview`}
                        >
                          <div>{SVG.document}</div>
                          {upFile.name && (
                            <span>
                              {upFile.name.split(' ').slice(0, 20).join(' ')}
                              {upFile.name.split(' ').length > 20 ? '...' : ''}
                            </span>
                          )}

                          <h6
                            style={{ cursor: 'pointer' }}
                            onClick={() => handelClear(index)}
                            className="m-0"
                          >
                            {' '}
                            X{' '}
                          </h6>
                        </div>
                      </div>
                    </div>
                  );
                })
              ) : (
                <></>
              ))}
            {/* );
          })} */}
          </div>
        )}
        {previewimage && previewposition === 'bottom' && (
          <div className="imagePreview imagePreview-custom d-flex justify-content-center mt-3">
            {preview.length > 0 ? (
              preview.map((upFile: any, index) => {
                // Specify the correct type 'File'
                return (
                  <div className={`previewImage ${Styles.previewImageWrap}`} key={index}>
                    <span
                      className={Styles.clearIcon}
                      role="button"
                      title="Remove"
                      onClick={() => handelClear(index)}
                    >
                      &times;
                    </span>
                    <img src={upFile.preview} alt="preview" />
                  </div>
                );
              })
            ) : (
              <div className={`previewImage ${Styles.previewImageWrap}`}>
                {imageSrc && (
                  <span
                    className={Styles.clearIcon}
                    role="button"
                    title="Remove"
                    onClick={handelClearImage}
                  >
                    &times;
                  </span>
                )}
                <img
                  src={imageSrc || Previeew}
                  alt="preview"
                  height="100%"
                  width="100%"
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    // Prevent infinite loop if the fallback also fails
                    target.onerror = null;
                    target.src = getPreviewFallback(imageSrc);
                  }}
                />
              </div>
            )}
          </div>
        )}
      </div>
    </React.Fragment>
  );
};

export default DropZoneMultiC;
